import { CartCount, fetchCart } from 'theme';

class BBVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.buttonShowMoreOptionValuesMobile = this.querySelector('[data-option-show-mobile]');
    this.hiddenOptionValuesMobile = this.querySelectorAll('[data-option-value-hidden-mobile]');
    this.productHandle = this.dataset.productHandle;
  }

  handleShowMoreOptionValuesMobile() {
    this.hiddenOptionValuesMobile?.forEach((elm) => {
      elm.removeAttribute('data-option-value-hidden-mobile');
    });
    this.buttonShowMoreOptionValuesMobile?.remove();
    localStorage.setItem('bb-variant-picker-show-all-mobile', this.productHandle);
  }

  async connectedCallback() {
    if (localStorage.getItem('bb-variant-picker-show-all-mobile') === this.productHandle) {
      this.handleShowMoreOptionValuesMobile();
    }
    this.buttonShowMoreOptionValuesMobile?.addEventListener('click', async () => {
      this.handleShowMoreOptionValuesMobile();
    });
  }
}
if (!customElements.get('bb-variant-picker')) {
  customElements.define('bb-variant-picker', BBVariantPicker);
}

class BBCountDown extends HTMLElement {
  static get observedAttributes() {
    return ['data-expiration'];
  }

  constructor() {
    super();
    this.intervalId = null;
    this.expirationDate = null;
    this.hasNotifiedExpiry = false;
    this.unitElements = null;
  }

  connectedCallback() {
    this.setAttribute('role', this.getAttribute('role') || 'timer');
    this.setAttribute('aria-live', this.getAttribute('aria-live') || 'polite');
    this.cacheUnitElements();
    this.refreshExpiration();
    this.startTicker();
  }

  disconnectedCallback() {
    this.stopTicker();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-expiration' && oldValue !== newValue) {
      this.refreshExpiration();
    }
  }

  cacheUnitElements() {
    this.unitElements = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
  }

  refreshExpiration() {
    this.stopTicker();
    this.hasNotifiedExpiry = false;
    this.classList.remove('is-expired');
    this.expirationDate = this.parseExpiration(this.dataset.expiration);
    this.updateDisplay();
    this.startTicker();
  }

  parseExpiration(rawValue) {
    if (!rawValue || typeof rawValue !== 'string') {
      return null;
    }

    var trimmed = rawValue.trim();
    if (!trimmed) {
      return null;
    }

    var normalized = trimmed.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      normalized = trimmed + 'T00:00';
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
      normalized += ':00';
    }

    var candidate = new Date(normalized);
    return isNaN(candidate.getTime()) ? null : candidate;
  }

  startTicker() {
    if (!this.expirationDate) {
      return;
    }
    if (this.intervalId !== null) {
      return;
    }

    this.intervalId = window.setInterval(
      function () {
        this.updateDisplay();
      }.bind(this),
      1000,
    );
  }

  stopTicker() {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  updateDisplay() {
    var units = this.calculateRemaining();
    this.renderUnits(units);

    if (units.totalMilliseconds <= 0 && !this.hasNotifiedExpiry) {
      this.hasNotifiedExpiry = true;
      this.classList.add('is-expired');
      this.stopTicker();
    } else if (units.totalMilliseconds > 0) {
      this.classList.remove('is-expired');
    }
  }

  calculateRemaining() {
    if (!this.expirationDate) {
      return {
        days: '--',
        hours: '--',
        minutes: '--',
        seconds: '--',
        totalMilliseconds: -1,
      };
    }

    var now = new Date();
    var totalMilliseconds = this.expirationDate.getTime() - now.getTime();
    var remaining = Math.max(totalMilliseconds, 0);

    var totalSeconds = Math.floor(remaining / 1000);
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    return {
      days: days,
      hours: hours,
      minutes: minutes,
      seconds: seconds,
      totalMilliseconds: totalMilliseconds,
    };
  }

  renderUnits(units) {
    this.cacheUnitElements();

    this.updateUnit(this.unitElements.days, units.days, false);
    this.updateUnit(this.unitElements.hours, units.hours, true);
    this.updateUnit(this.unitElements.minutes, units.minutes, true);
    this.updateUnit(this.unitElements.seconds, units.seconds, true);
  }

  updateUnit(target, value, pad) {
    if (!target) {
      return;
    }
    var output = value;
    if (typeof value === 'number' && pad) {
      output = this.padNumber(value);
    }
    if (typeof value === 'number' && !pad) {
      output = String(value);
    }
    if (typeof value === 'string') {
      output = value;
    }
    if (target.textContent !== output) {
      target.textContent = output;
    }
  }

  padNumber(value) {
    return value < 10 ? '0' + value : String(value);
  }
}

if (!customElements.get('bb-countdown')) {
  customElements.define('bb-countdown', BBCountDown);
}

class MediaGridNewRatingVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-media-grid-new-video]');
    this.playBtn = this.querySelector('[data-media-grid-new-play-btn]');
    this.thumb = this.querySelector('.media-grid-new-rating-video__thumbnail');

    if (!this.video) return;

    this._onPlay = this._updatePlayState.bind(this);
    this._onPause = this._updatePlayState.bind(this);
    this._onEnded = this._onVideoEnded.bind(this);

    this.video.addEventListener('play', this._onPlay);
    this.video.addEventListener('pause', this._onPause);
    this.video.addEventListener('ended', this._onEnded);

    if (this.playBtn) {
      this._onPlayBtnClick = this._togglePlay.bind(this);
      this.playBtn.addEventListener('click', this._onPlayBtnClick);
    }

    if (!this.video.hasAttribute('controls')) {
      var mediaArea = this.querySelector('.media-grid-new-rating-video__media');
      if (mediaArea) {
        this._onMediaClick = function (e) {
          if (e.target.closest('[data-media-grid-new-play-btn]')) return;
          e.preventDefault();
          e.stopPropagation();
          this._togglePlay(e);
        }.bind(this);
        mediaArea.addEventListener('click', this._onMediaClick);
        this._mediaArea = mediaArea;
      }
    }

    this._updatePlayState();
  }

  disconnectedCallback() {
    if (this.video) {
      this.video.removeEventListener('play', this._onPlay);
      this.video.removeEventListener('pause', this._onPause);
      this.video.removeEventListener('ended', this._onEnded);
    }
    if (this.playBtn && this._onPlayBtnClick) {
      this.playBtn.removeEventListener('click', this._onPlayBtnClick);
    }
    if (this._mediaArea && this._onMediaClick) {
      this._mediaArea.removeEventListener('click', this._onMediaClick);
    }
  }

  _togglePlay(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.video.paused) {
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  _updatePlayState() {
    if (!this.playBtn) return;
    if (this.video.paused) {
      this.playBtn.classList.remove('media-grid-new-rating-video__play-btn--hidden');
    } else {
      this.playBtn.classList.add('media-grid-new-rating-video__play-btn--hidden');
      if (this.thumb) this.thumb.classList.add('media-grid-new-rating-video__thumbnail--hidden');
    }
  }

  _onVideoEnded() {
    if (this.playBtn) this.playBtn.classList.remove('media-grid-new-rating-video__play-btn--hidden');
  }
}

if (!customElements.get('media-grid-new-rating-video')) {
  customElements.define('media-grid-new-rating-video', MediaGridNewRatingVideo);
}

class MediaGridNewCollapseWrapper extends HTMLElement {
  connectedCallback() {
    this.expandBtn = this.querySelector('[data-media-grid-new-expand]');
    if (!this.expandBtn) return;

    this._onExpand = this._expand.bind(this);
    this.expandBtn.addEventListener('click', this._onExpand);
  }

  disconnectedCallback() {
    if (this.expandBtn && this._onExpand) {
      this.expandBtn.removeEventListener('click', this._onExpand);
    }
  }

  _expand() {
    var fullHeight = this.scrollHeight;
    this.style.maxHeight = fullHeight + 'px';
    this.classList.add('media-grid-new-expanded');

    if (this.expandBtn) {
      this.expandBtn.setAttribute('aria-expanded', 'true');
      this.expandBtn.setAttribute('aria-hidden', 'true');
      this.expandBtn.style.opacity = '0';
      this.expandBtn.style.pointerEvents = 'none';
    }

    var gradient = this.querySelector('.media-grid-new-collapse-gradient');
    if (gradient) {
      gradient.setAttribute('aria-hidden', 'true');
      gradient.style.opacity = '0';
    }

    this._onTransitionEnd = function () {
      this.style.maxHeight = 'none';
      this.removeEventListener('transitionend', this._onTransitionEnd);
    }.bind(this);

    this.addEventListener('transitionend', this._onTransitionEnd);
  }
}

if (!customElements.get('media-grid-new-collapse-wrapper')) {
  customElements.define('media-grid-new-collapse-wrapper', MediaGridNewCollapseWrapper);
}

// Pair-as-one cart count, same rule as snippets/bb-cart-count.liquid and the order-matrix bar: a configured bottle's
// nested finishing-charge line (_config_id without _config) is a service fee on that bottle, not a product.
function bbCartCount(cart) {
  if (!Array.isArray(cart.items)) return cart.item_count;
  return cart.items.reduce((count, line) => {
    const props = line.properties || {};
    return props._config_id && !props._config ? count : count + line.quantity;
  }, 0);
}

// Impact's <cart-count> writes the raw cart.item_count. This subclass keeps its markup and animation and only swaps
// the count. It's a separate tag because <cart-count> is already upgraded, with its listeners bound, when this runs.
class BBCartCount extends CartCount {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;
    document.addEventListener('cart:change', (event) => (this.itemCount = bbCartCount(event.detail.cart)), { signal });
    document.addEventListener('cart:refresh', () => this._updateFromServer(), { signal });
    window.addEventListener('pageshow', () => this._updateFromServer(), { signal });
  }

  async _updateFromServer() {
    // The theme's shared cart promise (live binding), renewed on cart:refresh and bfcache restores.
    this.itemCount = bbCartCount(await fetchCart);
  }
}

if (!customElements.get('bb-cart-count')) {
  customElements.define('bb-cart-count', BBCartCount);
}
